import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api, getJourney } from "../lib/api";
import { PageShell } from "../components/PageShell";
import { useQuantum } from "../components/QuantumTransitions";
import { formatApiError } from "../context/AuthContext";

const CATS = [
  { key: "money", label: "Money & financial choices", color: "#00F0FF", ph: "I started investing at 30…" },
  { key: "career", label: "Career & professional ambitions", color: "#B026FF", ph: "I stayed in teaching instead of law school…" },
  { key: "love", label: "Love & romantic relationships", color: "#FF6AD5", ph: "I'm thinking about moving in with my partner…" },
  { key: "family", label: "Family & lifestyle", color: "#FFD700", ph: "Whether to have children…" },
  { key: "custom", label: "Your own decision", color: "#7CFFB2", ph: "I wish I had learned piano…" },
];
const KINDS = [["made", "Already made"], ["regret", "I regret"], ["considering", "Considering"], ["wish", "Wish I had"]];

export default function Decisions() {
  const { id } = useParams();
  const { go, withCollapse } = useQuantum();
  const [items, setItems] = useState(CATS.map((c) => ({ category: c.key, text: "", kind: "made" })));

  useEffect(() => {
    getJourney(id).then((j) => j.five_decisions?.length && setItems(CATS.map((c) => j.five_decisions.find((d) => d.category === c.key) || { category: c.key, text: "", kind: "made" })));
  }, [id]);

  const set = (i, patch) => setItems(items.map((it, k) => (k === i ? { ...it, ...patch } : it)));

  const save = async () => {
    try {
      await withCollapse("Collapsing your possibilities into five decisions…", () => api.post(`/journey/${id}/five-decisions`, { decisions: items.filter((d) => d.text.trim()) }));
      go(`/multiverse/${id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <PageShell back={`/journey/${id}`} label="The Five Decisions" title={<>Every choice opens a <span className="text-glow-purple">possibility.</span></>}
      subtitle="What if you could meet the version of yourself who chose differently? Name five decisions: ones you made, regret, are considering, or wish you had made.">
      <div className="space-y-4">
        {CATS.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
            className="glass rounded-2xl p-5" style={{ borderColor: `${c.color}44` }} data-testid={`decision-${c.key}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="font-mono-label" style={{ color: c.color }}>0{i + 1} · {c.label}</div>
              <div className="flex gap-1 flex-wrap">
                {KINDS.map(([k, l]) => (
                  <button key={k} onClick={() => set(i, { kind: k })} data-testid={`decision-${c.key}-kind-${k}`}
                    className="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                    style={{ borderColor: items[i].kind === k ? c.color : "rgba(255,255,255,0.1)", color: items[i].kind === k ? c.color : "rgba(255,255,255,0.5)" }}>{l}</button>
                ))}
              </div>
            </div>
            <textarea rows={2} value={items[i].text} onChange={(e) => set(i, { text: e.target.value })} placeholder={c.ph} data-testid={`decision-${c.key}-text`}
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/60" />
          </motion.div>
        ))}
      </div>
      <button onClick={save} className="btn-pill btn-pill-primary mt-8 w-full justify-center" data-testid="decisions-save-btn">
        Open my multiverse <ArrowRight size={16} />
      </button>
    </PageShell>
  );
}
